import { Collapsible } from '@chakra-ui/react';
import { IQuoteTemplateConfigSchema } from '@monetize/types/app';
import {
  QuoteTemplateSettingPermission,
  QuoteTemplateSettingsConfig,
} from '@monetize/types/experience-config';
import { useState } from 'react';
import {
  Control,
  Controller,
  FieldPath,
  UseFormSetValue,
  useFormState,
  useWatch,
} from 'react-hook-form';
import { MdExpandMore, MdInfo } from 'react-icons/md';
import {
  MBox,
  MFlex,
  MIcon,
  MIconButton,
  MSwitch,
  MText,
  MTooltip,
} from '../../Monetize';
import { QuoteTemplateAccessControl } from './QuoteTemplateAccessControl';
import { QuoteTemplateSettingMeta } from './quoteTemplateSettings.metadata';
import {
  getSettingAccessSummary,
  hasAnyDirtyField,
} from './quoteTemplateSettings.utils';

interface QuoteTemplateSettingCardProps {
  meta: QuoteTemplateSettingMeta;
  valuesControl: Control<IQuoteTemplateConfigSchema>;
  permissionsControl: Control<QuoteTemplateSettingsConfig>;
  permissionsSetValue: UseFormSetValue<QuoteTemplateSettingsConfig>;
  isDisabled?: boolean;
}

/**
 * A single setting on the Quote Templates settings page: the label, an
 * access-scope summary, and the default-for-new-quotes toggle (bound to the
 * quote-template values form) in the header, and a collapsible body with the
 * role-based Access Scope controls (bound to the experience-config permissions
 * form).
 */
export const QuoteTemplateSettingCard = ({
  meta,
  valuesControl,
  permissionsControl,
  permissionsSetValue,
  isDisabled,
}: QuoteTemplateSettingCardProps) => {
  const [isExpanded, setIsExpanded] = useState(false);

  // The name is a dynamic record path, so RHF widens the value type; the
  // resolved runtime value is the setting's permission entry.
  const permission = useWatch({
    control: permissionsControl,
    name: `settings.${meta.key}` as FieldPath<QuoteTemplateSettingsConfig>,
  }) as unknown as QuoteTemplateSettingPermission | undefined;

  const { dirtyFields: valuesDirtyFields } = useFormState({
    control: valuesControl,
  });
  const { dirtyFields: permissionsDirtyFields } = useFormState({
    control: permissionsControl,
  });
  const isSettingDirty =
    hasAnyDirtyField(valuesDirtyFields[meta.key]) ||
    hasAnyDirtyField(permissionsDirtyFields.settings?.[meta.key]);

  // A nested setting has no effect while its parent is off, so its default
  // shows as off and disabled then. Not subscribed for top-level settings.
  const parentValue = useWatch({
    control: valuesControl,
    name: meta.parentKey ?? meta.key,
    disabled: !meta.parentKey,
  });
  const isParentOff = !!meta.parentKey && !parentValue;

  return (
    <MBox
      data-testid={`quoteTemplateSetting-card-${meta.key}`}
      data-dirty={isSettingDirty || undefined}
      ml={meta.parentKey ? 4 : undefined}
      borderWidth="1px"
      borderColor="tGray.back"
      borderRadius="md"
      overflow="hidden"
      // Unsaved indicator: the recolored 1px border + 2px inset shadow form
      // the same 3px orange strip as the Input `unsaved` variant, without
      // shifting the card content the way a wider border would.
      borderLeftColor={isSettingDirty ? 'tOrange.base' : 'tGray.back'}
      boxShadow={
        isSettingDirty ? 'inset 2px 0 0 {colors.tOrange.base}' : undefined
      }
    >
      <MFlex alignItems="center" justifyContent="space-between" px={4} py={2}>
        {/* Label + access summary stack in the left column so the summary reads
            as a description of the setting, not a caption for the toggle on the
            right. */}
        <MBox>
          {/* Icon rendered inline so it stays attached to the end of the label
              when the label wraps, rather than floating to the right. */}
          <MText fontWeight="600">
            {meta.label}
            {!!meta.tooltip && (
              <MTooltip label={meta.tooltip} placement="top" shouldWrapChildren>
                <MIcon
                  as={MdInfo}
                  color="tPurple.base"
                  boxSize={4}
                  ml={1}
                  verticalAlign="text-bottom"
                />
              </MTooltip>
            )}
          </MText>
          <MText fontSize="sm" color="tGray.darkPurple" mt={0.5}>
            {getSettingAccessSummary(permission)}
          </MText>
        </MBox>

        <MFlex alignItems="center" gap={3}>
          <Controller
            control={valuesControl}
            name={meta.key}
            render={({ field: { value, onChange, ...rest } }) => {
              const boolValue = value as boolean | undefined;
              const isStoredChecked = meta.inverted
                ? !boolValue
                : meta.defaultCheckedWhenUndefined
                  ? (boolValue ?? true)
                  : !!boolValue;
              return (
                <MSwitch
                  isChecked={!isParentOff && isStoredChecked}
                  isDisabled={isDisabled || isParentOff}
                  onChange={(ev) =>
                    onChange(
                      meta.inverted ? !ev.target.checked : ev.target.checked,
                    )
                  }
                  {...rest}
                />
              );
            }}
          />
          <MIconButton
            aria-label={`${isExpanded ? 'Collapse' : 'Expand'} ${meta.label} access settings`}
            data-testid={`quoteTemplateSetting-expand-${meta.key}`}
            variant="ghost"
            size="sm"
            icon={
              <MIcon
                as={MdExpandMore}
                boxSize={5}
                transform={isExpanded ? 'rotate(180deg)' : undefined}
                transition="transform 0.2s"
              />
            }
            onClick={() => setIsExpanded((prev) => !prev)}
          />
        </MFlex>
      </MFlex>

      <Collapsible.Root open={isExpanded} lazyMount unmountOnExit>
        <Collapsible.Content>
          <MBox
            px={4}
            pt={3}
            pb={3}
            borderTopWidth="1px"
            borderColor="tGray.back"
          >
            <QuoteTemplateAccessControl
              settingKey={meta.key}
              control={permissionsControl}
              setValue={permissionsSetValue}
              isDisabled={isDisabled}
            />
          </MBox>
        </Collapsible.Content>
      </Collapsible.Root>
    </MBox>
  );
};
